import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import { Reveal } from "@/components/Reveal";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { dayWindow, windowLabel } from "@/lib/hours";
import { findNearestOpenBranch } from "@/lib/branches";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Bike, Store, Clock, Gift } from "lucide-react";

const SORTS = [
  { v: "popular", label: "Popular" },
  { v: "new", label: "Newest" },
  { v: "price_asc", label: "Price: Low" },
  { v: "price_desc", label: "Price: High" },
];

export default function Menu() {
  const [params, setParams] = useSearchParams();
  useSeo({
    title: "Menu | Metros Fried Chicken Takeaway in Epsom",
    description: "Browse the full Metros menu — fried chicken, chicken burgers, wings, wraps and sides. Order online for takeaway collection or food delivery in Epsom, Surrey.",
    keywords: "fried chicken menu, chicken burger, chicken wings, sides, takeaway Epsom, food delivery Epsom, Epsom Surrey, Metros fried chicken",
    path: "/menu",
    image: "https://mymetros.co.uk/logo.png",
  });
  const { settings } = useSettings();
  const { selectedBranch, branchStatus, setSelectedBranch, postcode } = useCart();
  const { user } = useAuth();
  const [reward, setReward] = useState(null);
  const delivW = dayWindow(settings, "delivery");
  const collW = dayWindow(settings, "collection");
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(params.get("category") || "Popular");
  const [offerId, setOfferId] = useState(params.get("offer_id") || null);
  const [offerTitle, setOfferTitle] = useState("");
  const [offer, setOffer] = useState(null);
  const [search, setSearch] = useState(params.get("search") || "");
  const [sort, setSort] = useState("popular");
  const [filter, setFilter] = useState(null); // popular | new | offers | vegetarian
  const [modal, setModal] = useState(null);
  const railRef = useRef(null);
  const [rail, setRail] = useState({ hasOverflow: false, atStart: true, atEnd: false });
  const updateRail = () => {
    const el = railRef.current; if (!el) return;
    setRail({
      hasOverflow: el.scrollWidth > el.clientWidth + 2,
      atStart: el.scrollLeft <= 2,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };
  const scrollRail = (dir) => { const el = railRef.current; if (!el) return; el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: "smooth" }); };
  useEffect(() => {
    updateRail();
    window.addEventListener("resize", updateRail);
    return () => window.removeEventListener("resize", updateRail);
  }, [cats]);
  const [switching, setSwitching] = useState(false);

  const switchToNearestOpen = async () => {
    setSwitching(true);
    const b = await findNearestOpenBranch({ postcode, excludeId: selectedBranch?.id });
    setSwitching(false);
    if (b) { setSelectedBranch({ id: b.id, name: b.name }); toast.success(`Switched to ${b.name.replace("METROS — ", "")}`); }
    else toast.error("No open branches nearby right now.");
  };

  useEffect(() => {
    if (!user || !user.id) { setReward(null); return; }
    api.get(`/account/rewards${selectedBranch ? `?branch_id=${encodeURIComponent(selectedBranch.id)}` : ""}`).then(({ data }) => setReward(data?.order_reward || null)).catch(() => setReward(null));
  }, [user, selectedBranch]);

  useEffect(() => {
    const q = selectedBranch ? `?branch_id=${encodeURIComponent(selectedBranch.id)}` : "";
    api.get(`/menu/categories${q}`).then(({ data }) => setCats([{ name: "Popular", slug: "popular" }, ...data])).catch(() => {});
  }, [selectedBranch]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (offerId) {
      q.set("offer_id", offerId);
      if (selectedBranch) q.set("branch_id", selectedBranch.id);
      api.get(`/menu/products?${q.toString()}`).then(({ data }) => { setProducts(data); setLoading(false); }).catch(() => { setProducts([]); setLoading(false); });
      return;
    }
    if (search) q.set("search", search);
    else if (active === "Popular") q.set("popular", "true");
    else if (active) q.set("category", active);
    if (sort) q.set("sort", sort);
    if (filter === "new") q.set("new", "true");
    if (filter === "vegetarian") q.set("dietary", "Vegetarian");
    if (selectedBranch) q.set("branch_id", selectedBranch.id);
    api.get(`/menu/products?${q.toString()}`).then(({ data }) => { setProducts(data); setLoading(false); }).catch(() => { setProducts([]); setLoading(false); });
  }, [active, search, sort, filter, selectedBranch, offerId]);

  useEffect(() => {
    if (!offerId) { setOfferTitle(""); setOffer(null); return; }
    api.get(`/offers${selectedBranch ? `?branch_id=${selectedBranch.id}` : ""}`).then(({ data }) => {
      const o = data.find((x) => x.id === offerId);
      setOfferTitle(o ? o.title : "");
      setOffer(o || null);
    }).catch(() => {});
  }, [offerId, selectedBranch]);

  const selectCat = (name) => {
    setActive(name); setSearch(""); setOfferId(null);
    const p = new URLSearchParams(); if (name !== "Popular") p.set("category", name); setParams(p);
  };

  const offerUnitPrice = offerId && offer && (offer.pricing_mode || "per_item") !== "bundle"
    ? (Number(offer.discounted_price) || null) : null;

  return (
    <div>
      <div className="bg-metros-black text-white grain px-4 md:px-6 py-8 md:py-10">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight">Our Menu</h1>
          <p className="text-gray-400 mt-1">Freshly made to order. Add anything to your basket.</p>
        </div>
      </div>

      {settings && (
        <div data-testid="delivery-times-banner" className="bg-metros-black/95 text-white text-xs sm:text-sm px-4 md:px-6 py-2.5 flex items-center justify-center gap-4 md:gap-6">
          <span className="flex items-center gap-1.5"><Bike size={15} className="text-metros-red" /> Delivery <span className="font-bold bg-metros-red text-white px-2 py-0.5 rounded-md">{windowLabel(delivW)}</span></span>
          <span className="opacity-30">|</span>
          <span className="flex items-center gap-1.5"><Store size={15} className="text-metros-red" /> Collection <span className="font-bold bg-metros-red text-white px-2 py-0.5 rounded-md">{windowLabel(collW)}</span></span>
        </div>
      )}

      {!user && settings?.order_reward?.enabled && settings.order_reward.percent > 0 && (
        <div data-testid="menu-guest-reward-promo" className="bg-metros-red text-white text-sm sm:text-base font-semibold px-4 md:px-6 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
          <Gift size={18} className="shrink-0" />
          <span>Order online &amp; every <b>{settings.order_reward.every || 5}th order</b> gets <b>{settings.order_reward.percent}% off</b> —</span>
          <Link to="/login" data-testid="menu-guest-reward-signin" className="underline font-bold hover:text-white/80 transition-colors">sign in to start earning!</Link>
        </div>
      )}

      {reward?.enabled && reward.percent > 0 && reward.until_reward <= 1 && (
        <div data-testid="menu-reward-reminder" className="bg-metros-red text-white text-sm sm:text-base font-semibold px-4 md:px-6 py-3 flex items-center justify-center gap-2 text-center">
          <Gift size={18} className="shrink-0" />
          {reward.is_next_reward || reward.until_reward === 0
            ? <span>Your next order gets <b>{reward.percent}% off</b> — it's applied automatically at checkout!</span>
            : <span>Just <b>1 more order</b> and you'll unlock <b>{reward.percent}% off</b> your next one!</span>}
        </div>
      )}

      {selectedBranch && branchStatus?.is_open === false && (
        <div data-testid="menu-closed-ribbon" className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 md:px-6 py-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
          <Clock size={15} className="text-amber-600 shrink-0" />
          <span><b>{selectedBranch.name.replace("METROS — ", "")}</b> is closed right now{branchStatus.opens_at ? ` · opens at ${branchStatus.opens_at}` : ""}. You can still browse and schedule an order.</span>
          <button data-testid="menu-switch-open" onClick={switchToNearestOpen} disabled={switching}
            className="inline-flex items-center gap-1 text-metros-red font-bold underline disabled:opacity-60">
            {switching && <Loader2 className="animate-spin" size={13} />} Switch to nearest open branch
          </button>
        </div>
      )}

      {offerId && offerTitle && (
        <div data-testid="menu-offer-banner" className="bg-metros-red text-white text-sm px-4 md:px-6 py-2.5 flex items-center justify-center gap-3">
          <span>Showing items in <b>{offerTitle}</b></span>
          <button data-testid="menu-offer-clear" onClick={() => selectCat("Popular")} className="underline font-semibold">View full menu</button>
        </div>
      )}

      {/* Sticky category rail + search */}
      <div className="sticky top-0 md:top-[70px] z-[80] bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="menu-search" value={search} onChange={(e) => { setSearch(e.target.value); if (offerId) setOfferId(null); }}
              placeholder="Search products, ingredients…"
              className="w-full bg-gray-100 rounded-full pl-9 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </div>
          {reward?.enabled && reward.percent > 0 && reward.until_reward > 1 && (
            <div data-testid="menu-reward-chip" className="mb-3 inline-flex items-center gap-1.5 bg-metros-red/10 text-metros-red border border-metros-red/30 rounded-full px-3 py-1 text-xs font-bold">
              <Gift size={13} /> {reward.until_reward} orders to {reward.completed === 0 ? `your first ${reward.percent}% reward` : `${reward.percent}% off`}
            </div>
          )}
          <div className="relative">
            {rail.hasOverflow && (
              <button type="button" onClick={() => scrollRail(-1)} disabled={rail.atStart}
                aria-label="Scroll categories left" data-testid="cat-scroll-left"
                className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white shadow-md border border-gray-200 text-metros-black hover:bg-metros-red hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none">
                <ChevronLeft size={20} />
              </button>
            )}
            <div ref={railRef} onScroll={updateRail} className={`flex gap-2 overflow-x-auto no-scrollbar ${rail.hasOverflow ? "md:px-11" : ""}`}>
              {cats.map((c) => (
                <button key={c.name} data-testid={`cat-pill-${c.slug || c.name}`} onClick={() => selectCat(c.name)}
                  className={`px-5 py-2 rounded-full font-medium whitespace-nowrap text-sm transition-colors ${active === c.name && !search ? "bg-metros-black text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                  {c.name}
                </button>
              ))}
            </div>
            {rail.hasOverflow && (
              <button type="button" onClick={() => scrollRail(1)} disabled={rail.atEnd}
                aria-label="Scroll categories right" data-testid="cat-scroll-right"
                className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white shadow-md border border-gray-200 text-metros-black hover:bg-metros-red hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none">
                <ChevronRight size={20} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-3 overflow-x-auto no-scrollbar">
            <SlidersHorizontal size={15} className="text-gray-400 shrink-0" />
            {["new", "vegetarian"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(filter === f ? null : f)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${filter === f ? "bg-metros-red text-white border-metros-red" : "border-gray-200 text-gray-600"}`}>
                {f === "new" ? "New" : "Vegetarian"}
              </button>
            ))}
            <span className="w-px h-5 bg-gray-200 mx-1 shrink-0" />
            {SORTS.map((so) => (
              <button key={so.v} data-testid={`sort-${so.v}`} onClick={() => setSort(so.v)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${sort === so.v ? "bg-metros-black text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                {so.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-metros-red" size={32} /></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="no-products">
            <p className="font-heading font-bold text-xl">No items found</p>
            <p className="text-muted-foreground text-sm mt-1">Try a different category or search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.08} amount={0.1}>
                <ProductCard product={p} onOpen={setModal} offerPrice={offerUnitPrice} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <ProductModal product={modal} open={!!modal} onClose={() => setModal(null)} offerId={offerId} offerPrice={offerUnitPrice} offerTitle={offerTitle} />
    </div>
  );
}
